import React, { useState } from "react";
import { Box, Button, Popover, Tooltip, Typography } from "@mui/material";
import { useDroppable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { VoicesCalendarPost } from "../../../Models/voicesCalendar";
import VoicesCalendarPostChip from "./VoicesCalendarPostChip";
import { dayKey } from "./voicesCalendarDates";

interface Props {
  day: Date;
  posts: VoicesCalendarPost[];
  outsideMonth: boolean;
  tall: boolean;
  onOpen: (postId: number) => void;
  onPickDay: (day: Date) => void;
}

const MONTH_VISIBLE_POSTS = 3;

const VoicesCalendarDay: React.FC<Props> = ({
  day,
  posts,
  outsideMonth,
  tall,
  onOpen,
  onPickDay,
}) => {
  const { t, i18n } = useTranslation("voicesPosts");
  const { colors: workspace } = useWorkspaceColors();
  const { setNodeRef, isOver } = useDroppable({ id: dayKey(day) });
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  const isToday = dayKey(day) === dayKey(new Date());
  const weekend = day.getDay() === 0 || day.getDay() === 6;
  const shown = tall ? posts : posts.slice(0, MONTH_VISIBLE_POSTS);
  const hidden = posts.length - shown.length;

  const open = (postId: number) => {
    setMoreAnchor(null);
    onOpen(postId);
  };

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: tall ? 360 : 120,
        p: 0.75,
        borderRight: `1px solid ${color.gray200}`,
        borderBottom: `1px solid ${color.gray200}`,
        backgroundColor: isOver ? color.orange50 : weekend ? color.gray50 : "white",
        opacity: outsideMonth ? 0.55 : 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Tooltip title={t("calendar_openDay")} enterDelay={400} disableInteractive>
          <Box
            component="button"
            type="button"
            onClick={() => onPickDay(day)}
            sx={{
              px: 0.75,
              border: 0,
              borderRadius: 1,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 12,
              lineHeight: "20px",
              fontWeight: isToday ? 700 : 500,
              color: isToday ? "white" : color.gray700,
              backgroundColor: isToday ? workspace.accent : "transparent",
              "&:hover": { backgroundColor: isToday ? workspace.accent : color.gray100 },
            }}
          >
            {day.getDate()}
          </Box>
        </Tooltip>
        {posts.length > MONTH_VISIBLE_POSTS && !tall && (
          <Typography variant="caption" sx={{ color: color.gray500 }}>
            {posts.length}
          </Typography>
        )}
      </Box>
      {shown.map((post) => (
        <VoicesCalendarPostChip key={post.id} post={post} compact={!tall} onOpen={open} />
      ))}
      {hidden > 0 && (
        <Button
          size="small"
          onClick={(event) => setMoreAnchor(event.currentTarget)}
          sx={{
            textTransform: "none",
            color: color.orange600,
            p: 0,
            minWidth: 0,
            fontSize: "0.72rem",
          }}
        >
          {t("calendar_more", { count: hidden })}
        </Button>
      )}
      <Popover
        open={!!moreAnchor}
        anchorEl={moreAnchor}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { p: 1.5, width: 280, maxHeight: 420, backgroundColor: "white" } }}
      >
        <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 1 }}>
          {day.toLocaleDateString(i18n.language, { weekday: "long", day: "numeric", month: "long" })}
        </Typography>
        {posts.map((post) => (
          <VoicesCalendarPostChip key={post.id} post={post} compact={false} onOpen={open} />
        ))}
      </Popover>
    </Box>
  );
};

export default VoicesCalendarDay;
